import { biText } from '@euler/app/components/message/widgets/bi-visualization/functions/biText';
import { BiQueryResult } from '@euler/model/bi/result';
import {
  BiAxisSide,
  BiChartViz,
  BiVizSpec,
  BiVizType,
  biSeriesTypeOf,
  isBiChartViz,
} from '@euler/model/bi/viz';
import { memo } from 'react';
import { EditorSection } from '../EditorSection';
import {
  BiSeriesLook,
  biCanGoRight,
  biIsComboType,
} from '../functions/biEditorCombo';
import { SeriesRow } from './SeriesRow';

function nameOf(chart: BiChartViz, key: string, result: BiQueryResult | null) {
  const own = chart.series.find(entry => entry.key === key)?.name;
  const column = result?.columns.find(entry => entry.key === key);
  return own ?? (column ? biText(column.label) : key);
}

/**
 * Combo charts (line, area, bar, stacked bar): each measure drawn as bars,
 * a line or an area, on the left or the right axis.
 */
export const SeriesSection = memo(
  ({
    viz,
    looks,
    result,
    onChange,
  }: {
    viz: BiVizSpec | null;
    looks: Record<string, BiSeriesLook> | undefined;
    result: BiQueryResult | null;
    onChange: (looks: Record<string, BiSeriesLook>) => void;
  }) => {
    if (!viz || !isBiChartViz(viz) || !biIsComboType(viz.type)) return null;
    // The chart's own type and the left axis are what an omitted look
    // means, so picking them clears the look rather than spelling it out.
    const plain = biSeriesTypeOf(viz, { key: '' });
    const set = (key: string, patch: BiSeriesLook) => {
      const merged = { ...looks?.[key], ...patch };
      const look: BiSeriesLook = {
        ...(merged.as && merged.as !== plain ? { as: merged.as } : {}),
        ...(merged.axis === BiAxisSide.Right ? { axis: merged.axis } : {}),
      };
      const rest = Object.fromEntries(
        Object.entries(looks ?? {}).filter(([other]) => other !== key),
      );
      onChange(Object.keys(look).length ? { ...rest, [key]: look } : rest);
    };
    return (
      <EditorSection
        title="系列"
        note={
          viz.seriesBy
            ? '按维度拆分的系列共用一种画法'
            : viz.type === BiVizType.StackedBar
            ? '柱形堆叠在左轴；折线和面积可用右轴'
            : '可混合柱形、折线和面积，并用右轴'
        }
      >
        {viz.series.map(entry => (
          <SeriesRow
            key={entry.key}
            name={nameOf(viz, entry.key, result)}
            type={biSeriesTypeOf(viz, entry)}
            axis={entry.axis ?? BiAxisSide.Left}
            canGoRight={biCanGoRight(viz, entry)}
            withAxis={!viz.seriesBy}
            onType={as => set(entry.key, { as })}
            onAxis={axis => set(entry.key, { axis })}
          />
        ))}
      </EditorSection>
    );
  },
);
